import type { Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import { useValuationsStore } from '@/pages/ValuationsV2/store/valuationsStore';
import { combineSx } from '@/utils/combineSx';
import { useTimelineEvents } from './useTimelineEvents';

const timelineStyles = {
  container: {
    containerType: 'inline-size',

    '@container (width > clamp(520px, 20vw, 1300px))': {
      '& div[data-id="timeline-events-wrapper"]': {
        gridTemplateColumns: '1fr',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
      },

      '& div[data-id="timeline-event-content"]': {
        placeItems: 'center',
        gridTemplateRows: '1fr auto 1fr',
        gridTemplateAreas: `"label"
                        "marker"
                        "date"
    `,
      },
    },

    '@container (width < clamp(520px, 20vw, 1300px))': {
      '& div[data-id="timeline-line"]': {
        width: '1px',
        height: '100%',
        top: '50%',
        left: '0.25rem',
        translate: '-50% -50% 0',
      },

      '& div[data-id="timeline-events-wrapper"]': {
        gridTemplateRows: '1fr',
        gridAutoFlow: 'row',
        gridAutoRows: '1fr',
        gap: '2rem',
      },

      '& div[data-id="timeline-event-content"]': {
        placeItems: 'center start',
        gridTemplateColumns: 'min-content',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
        gridTemplateAreas: `"marker label label"
                        "_ date date"
    `,
        textAlign: 'left',
      },
    },
  },
  eventsWrapper: {
    display: 'grid',
    width: '100%',
    position: 'relative',
    gap: '1rem',
    zIndex: 20,
  },

  timelineLine: {
    position: 'absolute',
    top: '50%',
    translate: '0 -50% 0',
    left: '0',
    right: '0',
    height: '1px',
    backgroundColor: (theme: Theme) => theme.palette.divider,
    zIndex: 10,
    opacity: 0.5,
  },

  eventContent: {
    position: 'relative',
    display: 'grid',
    gap: '0.5rem',
    width: '100%',
  },

  eventDate: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: (theme: Theme) => theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
    gridArea: 'date',
  },

  marker: (theme: Theme) => ({
    position: 'relative',
    zIndex: 20,
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: '50%',
    flexShrink: 0,
    gridArea: 'marker',
    '&[data-marker="active"]': {
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c100,
    },
    '&[data-marker="inactive"]': {
      backgroundColor: theme.palette.divider,
    },
  }),

  eventLabel: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: (theme: Theme) => theme.palette.text.secondary,
    lineHeight: 1,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    gridArea: 'label',
    textAlign: 'center',
  },
};

const TimelineView = () => {
  const selectedAssetDetails = useValuationsStore.use.selectedAssetDetails();
  const timelineEvents = useTimelineEvents(selectedAssetDetails);

  if (timelineEvents.length === 0) {
    return null;
  }

  return (
    <Box sx={timelineStyles.container} data-id='timeline-events-container'>
      <Box sx={timelineStyles.eventsWrapper} data-id='timeline-events-wrapper'>
        <Box sx={timelineStyles.timelineLine} data-id='timeline-line' />
        {timelineEvents.map(event => (
          <Box
            key={event.label}
            sx={combineSx(timelineStyles.eventContent, { opacity: event.date ? 1 : 0.5 })}
            data-id='timeline-event-content'
            data-marker={event.date ? 'active' : 'inactive'}
          >
            <Typography sx={timelineStyles.eventLabel} data-id='timeline-event-label'>
              {event.label}
            </Typography>
            <Box
              sx={timelineStyles.marker}
              data-marker={event.date ? 'active' : 'inactive'}
              data-id='timeline-event-marker'
              component='i'
            />
            <Typography sx={timelineStyles.eventDate} data-id='timeline-event-date'>
              {event.date || '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { TimelineView };
